import { generateGuid } from 'guid';
import EventLoggerProvider from 'EventLogger/EventLoggerProvider';
import { type AnyObject } from '@Types';
import EventEmitter from 'events';
import type TypedEmitter from 'typed-emitter';
import { type PlayerViewModel } from './types/PlayerViewModel.types';
import { type LessonIguanaObject, type FrameInterface } from './Lessons.types';
import { type ChallengeViewModel } from './Componentized.types';

type FrameEvents = {
    completed: () => void;
    giveHint: (event: MouseEvent | TouchEvent, challengeViewModel: ChallengeViewModel) => void;

    // see useFrameViewModel
    updated: () => void;
};

export class FrameViewModel<
    T extends FrameInterface = FrameInterface,
> extends (EventEmitter as new () => TypedEmitter<FrameEvents>) {
    public id!: string;
    private $$playerViewModel?: PlayerViewModel;
    private $$frame!: T;

    constructor(frame: T, _options?: Record<string, unknown>) {
        super();
        this.frame = frame;
        this.id = generateGuid();
    }

    // Publicly Accessible Properties
    // - Any of these can be overridden in subclasses

    // - complete
    // True when the learner is free to move on to the next frame.
    get complete(): boolean {
        return false;
    }

    // - readyToValidate
    // True when some answer has been selected, and the user must take an
    // action in order to discover whether the selected answer is correct or not.
    get readyToValidate(): boolean {
        return false;
    }

    // - hasInvalidAnswer
    // True when the learner has selected an invalidAnswer and is being
    // shown negative feedback.
    get hasInvalidAnswer(): boolean {
        return false;
    }

    // - waitingForAnswer
    // True when the learner needs to take some action.
    get waitingForAnswer(): boolean {
        return !this.readyToValidate && !this.complete;
    }

    // in order to handle some
    // logging in scope.$digest, angular was trying to toJson the
    // frameViewModel.  The circular reference in playerViewModel was then throwing an error.
    // This removes playerViewModel from the enumerable properties, alowing it to
    // be toJsonned.
    get playerViewModel(): PlayerViewModel | undefined {
        return this.$$playerViewModel;
    }

    set playerViewModel(val: PlayerViewModel | undefined) {
        this.$$playerViewModel = val;
    }

    // in order to handle some
    // logging in scope.$digest, angular was trying to toJson the
    // frameViewModel.  The circular reference in frame was then throwing an error.
    // This removes frame from the enumerable properties, alowing it to
    // be toJsonned.
    get frame(): T {
        return this.$$frame;
    }

    set frame(val: T) {
        this.$$frame = val;
    }

    get lesson(): LessonIguanaObject<true> | undefined {
        return this.frame.lesson();
    }

    get editorMode(): boolean {
        return this.playerViewModel?.editorMode ?? false;
    }

    get previewMode(): boolean {
        return this.playerViewModel?.previewMode ?? false;
    }

    get index(): number | undefined {
        return this.playerViewModel?.frames.indexOf(this.frame);
    }

    get isPractice(): boolean {
        return this.frame.isPractice;
    }

    get testOrAssessment(): boolean {
        return this.playerViewModel?.testOrAssessment ?? false;
    }

    get supportsReviewPreviousMaterialBot(): boolean {
        return (
            this.index === 0 &&
            // See FrameListPlayerViewModel.botLaunchOptions
            !!this.playerViewModel?.preLessonReviewMessage &&
            // For these last two requirements, see comment in FrameListPlayerViewModel._getBotContentInfo
            !!this.playerViewModel?.previousLessonOutline &&
            !!this.playerViewModel?.stream
        );
    }

    get supportsExplainScreen(): boolean {
        if (this.index == null) return false;

        if (this.index > 0 && !this.supportsReviewThisLesson) return true;

        // Admins use preview mode to help answer student questions, often on exams. Since exams often have challenges
        // on the first screen, and there is no previous lesson to review in that case, we should allow AI tutor on the first screen
        // in that case.
        if (this.previewMode) return true;

        return false;
    }

    get supportsReviewThisLesson(): boolean {
        return !!(this.playerViewModel?.onLastFrame && this.complete && this.playerViewModel?.postLessonReviewMessage);
    }

    get frameSupportsBot(): boolean {
        if (!this.playerViewModel?.lessonSupportsBot) return false;
        return this.supportsReviewPreviousMaterialBot || this.supportsExplainScreen || this.supportsReviewThisLesson;
    }

    get previousFrameIds(): string[] {
        return this.frame.previousFrameIds;
    }

    giveHint(): void {
        // no-op
    }

    toJasminePP(): string {
        return `FrameViewModel for frame: ${this.frame.id}`;
    }

    // can be overriden
    destroy(): void {}

    gotoNextFrame(): void {
        this.playerViewModel?.gotoNext();
    }

    skipFrame(): void {
        this.playerViewModel?.skipFrame();
    }

    log(eventType: string, extraPayload?: AnyObject, options?: AnyObject): unknown {
        const mergedPayload = {
            ...this.logInfo(),
            ...extraPayload,
        };
        if (this.playerViewModel) {
            return this.playerViewModel.log(eventType, mergedPayload, options);
        }
        const EventLogger = EventLoggerProvider.get();
        return EventLogger?.log(eventType, mergedPayload, options);
    }

    logInfo(): Record<string, unknown> {
        return {
            ...this.playerViewModel?.logInfo(),
            ...this.frame.logInfo(),
            frame_play_id: this.id,
        };
    }

    setBackgroundColor(): void {
        this.playerViewModel?.setBodyBackground('white');
    }
}
